import { useMemo, useState } from 'react';
import type {
  CallActivityDto,
  LiveEmployee,
  PointDelivery,
  RouteDto,
} from '@geotracker/contracts';
import { Card, CardHeader, Metric } from '@/components/ui/card';
import { Badge, FlagBadge } from '@/components/ui/badge';
import { DayTimeline } from '@/components/charts/day-timeline';
import { Trend, type TrendPoint } from '@/components/charts/trend';
import { cn } from '@/lib/utils';
import {
  formatAge,
  formatBattery,
  formatBearing,
  formatClock,
  formatCoordinates,
  formatDistance,
  formatDuration,
  formatLag,
  formatSpeed,
} from '@/lib/format';
import { AudioTab } from './audio';
import { DELIVERY_CSS, DELIVERY_LABEL, type IndexRange } from './map-view';
import { TrackingControls } from './tracking-controls';

type Tab = 'movement' | 'stops' | 'data' | 'calls' | 'audio' | 'control';

/**
 * Matches `DEFAULT_MOVEMENT_OPTIONS.maxGapSeconds` on the server, so the point
 * where the timeline shows a hatched "no data" band is the same point where the
 * charts break their line. Two thresholds would let a chart draw straight
 * through a stretch the band beside it calls missing.
 */
const GAP_BREAK_MS = 1800 * 1000;

const TABS: { id: Tab; label: string }[] = [
  { id: 'movement', label: 'Movement' },
  { id: 'stops', label: 'Stops' },
  { id: 'data', label: 'Data delivery' },
  { id: 'calls', label: 'Calls' },
  { id: 'audio', label: 'Voice' },
  { id: 'control', label: 'Control' },
];

interface Props {
  employee: LiveEmployee;
  route: RouteDto | null;
  routeLoading: boolean;
  calls: CallActivityDto | null;
  onSeek: (iso: string) => void;
  onFocusStop: (index: number) => void;
  /** Frames a stretch of trail on the map — an upload episode, an offline run. */
  onFocusRange: (range: IndexRange) => void;
  /** Switches the map to delivery colouring and optionally filters to one class. */
  onShowDelivery: (only?: PointDelivery) => void;
}

export function EmployeeDetail({
  employee,
  route,
  routeLoading,
  calls,
  onSeek,
  onFocusStop,
  onFocusRange,
  onShowDelivery,
}: Props) {
  const [tab, setTab] = useState<Tab>('movement');

  return (
    <Card className="overflow-hidden">
      <CardHeader
        title={employee.employee.name}
        hint={
          <span>
            {employee.employee.code}
            {employee.employee.designation ? ` · ${employee.employee.designation}` : ''}
          </span>
        }
        action={<PresenceBadge employee={employee} />}
      />

      <div className="flex gap-1 border-b border-border px-3">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              '-mb-px border-b-2 px-2.5 py-2 text-xs font-medium transition-colors',
              tab === t.id
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t.label}
            {t.id === 'stops' && route ? (
              <span className="ml-1 tabular-nums opacity-60">{route.stops.length}</span>
            ) : null}
            {t.id === 'calls' && calls ? (
              <span className="ml-1 tabular-nums opacity-60">{calls.summary.total}</span>
            ) : null}
            {/*
             * A dot, not a count. The number of late fixes is not the thing an
             * admin needs on a tab strip — "some of today did not arrive live"
             * is, and it should be visible without opening the tab.
             */}
            {t.id === 'data' && route && route.summary.backfillPointCount > 0 ? (
              <span
                className="ml-1 inline-block h-1.5 w-1.5 rounded-full align-middle"
                style={{ background: DELIVERY_CSS.backfill }}
                aria-label="Some of this day was backfilled"
              />
            ) : null}
          </button>
        ))}
      </div>

      <div className="p-4">
        {tab === 'movement' ? (
          <MovementTab
            employee={employee}
            route={route}
            loading={routeLoading}
            onSeek={onSeek}
          />
        ) : null}
        {tab === 'stops' ? (
          <StopsTab route={route} loading={routeLoading} onFocusStop={onFocusStop} />
        ) : null}
        {tab === 'data' ? (
          <DeliveryTab
            route={route}
            loading={routeLoading}
            onFocusRange={onFocusRange}
            onShowDelivery={onShowDelivery}
          />
        ) : null}
        {tab === 'calls' ? <CallsTab calls={calls} /> : null}
        {tab === 'audio' ? (
          <AudioTab employeeId={employee.employee.id} date={route?.date ?? ''} />
        ) : null}
        {tab === 'control' ? (
          <TrackingControls
            employeeId={employee.employee.id}
            employeeName={employee.employee.name}
            sessionStatus={employee.state.sessionStatus}
          />
        ) : null}
      </div>
    </Card>
  );
}

/**
 * The current state, in one badge.
 *
 * Stationary time and position age are shown as two separate facts on purpose.
 * "Stationary 40m" from a fix that arrived four seconds ago is an observation;
 * the same words from a fix two hours old is a guess, and the second number is
 * what tells them apart.
 */
function PresenceBadge({ employee }: { employee: LiveEmployee }) {
  const moving = employee.movement.state === 'moving';

  return (
    <div className="text-right">
      <Badge tone={moving ? 'moving' : employee.movement.state === 'unknown' ? 'muted' : 'stopped'}>
        {moving
          ? `Moving · ${formatSpeed(employee.movement.speedMps)}`
          : employee.dwell
            ? `Stationary · ${formatDuration(employee.dwell.seconds)}`
            : employee.movement.state === 'unknown'
              ? 'No position'
              : 'Stationary'}
      </Badge>
      <div className="mt-1 text-2xs tabular-nums text-muted-foreground">
        fix {formatAge(employee.position?.ageSeconds)}
      </div>
    </div>
  );
}

function MovementTab({
  employee,
  route,
  loading,
  onSeek,
}: {
  employee: LiveEmployee;
  route: RouteDto | null;
  loading: boolean;
  onSeek: (iso: string) => void;
}) {
  const speedSeries = useMemo<TrendPoint[]>(() => {
    if (!route) return [];
    return route.points
      .filter((p) => p.spd !== null)
      .map((p) => ({
        t: p.t,
        value: Math.max(0, (p.spd ?? 0) * 3.6),
        label: formatClock(p.recordedAt, route.timezone),
      }));
  }, [route]);

  const batterySeries = useMemo<TrendPoint[]>(() => {
    if (!route) return [];
    return route.points
      .filter((p) => p.bat !== null)
      .map((p) => ({
        t: p.t,
        value: p.bat ?? 0,
        label: formatClock(p.recordedAt, route.timezone),
      }));
  }, [route]);

  return (
    <div className="space-y-5">
      <section>
        <h3 className="text-xs font-medium text-muted-foreground">Right now</h3>
        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Metric
            label="State"
            value={employee.movement.state === 'moving' ? 'Moving' : 'Stationary'}
            tone={employee.movement.state === 'moving' ? 'moving' : 'stopped'}
            sub={employee.dwell ? `since ${formatClock(employee.dwell.since)}` : undefined}
          />
          <Metric label="Speed" value={formatSpeed(employee.movement.speedMps)} />
          <Metric label="Heading" value={formatBearing(employee.movement.bearing)} />
          <Metric
            label="Battery"
            value={formatBattery(employee.state.batteryLevel, employee.state.isCharging)}
            sub={employee.state.networkType}
          />
        </div>

        {employee.dwell ? (
          <p className="mt-2 text-xs text-muted-foreground">
            Not moved for{' '}
            <span className="font-medium text-foreground">
              {formatDuration(employee.dwell.seconds)}
            </span>{' '}
            at{' '}
            <span className="font-medium text-foreground">
              {employee.dwell.place ??
                formatCoordinates(employee.dwell.latitude, employee.dwell.longitude)}
            </span>{' '}
            (within {employee.dwell.radiusMeters} m), measured to the newest fix —{' '}
            {formatAge(employee.position?.ageSeconds)}.
          </p>
        ) : null}

        {employee.flags.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-1">
            {employee.flags.map((flag) => (
              <FlagBadge key={flag} code={flag} />
            ))}
          </div>
        ) : null}
      </section>

      {loading ? (
        <p className="py-6 text-center text-sm text-muted-foreground">Loading the day…</p>
      ) : !route || route.points.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border py-6 text-center text-sm text-muted-foreground">
          No fixes recorded on this day ({route?.timezone ?? 'company time'}).
        </p>
      ) : (
        <>
          <section>
            <h3 className="text-xs font-medium text-muted-foreground">
              The day · {route.date}
            </h3>
            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Metric
                label="Distance"
                value={formatDistance(route.movement.distanceMeters)}
                sub={`${formatDistance(route.movement.movingDistanceMeters)} travelling`}
              />
              <Metric
                label="Moving"
                value={formatDuration(route.movement.movingSeconds)}
                tone="moving"
                sub={`avg ${formatSpeed(route.movement.avgSpeedMps)}`}
              />
              <Metric
                label="Stopped"
                value={formatDuration(route.movement.stoppedSeconds)}
                tone="stopped"
                sub={`${route.movement.stopCount} stop${route.movement.stopCount === 1 ? '' : 's'}`}
              />
              <Metric
                label="Paused"
                value={formatDuration(route.movement.idleSeconds)}
                tone="idle"
                sub="short halts"
              />
              <Metric
                label="Top speed"
                value={formatSpeed(route.movement.maxSpeedMps)}
                sub={
                  route.movement.maxSpeedAt
                    ? formatClock(route.movement.maxSpeedAt, route.timezone)
                    : undefined
                }
              />
              <Metric
                label="Longest stop"
                value={formatDuration(route.movement.longestStopSeconds)}
                sub={
                  route.movement.longStopCount > 0
                    ? `${route.movement.longStopCount} over 30m`
                    : undefined
                }
              />
              <Metric
                label="Tracked"
                value={formatDuration(route.movement.trackedSeconds)}
                sub={`${route.summary.pointCount} fixes`}
              />
              <Metric
                label="No data"
                value={formatDuration(route.movement.gapSeconds)}
                tone="gap"
                sub={route.gaps.length > 0 ? `${route.gaps.length} gaps` : 'complete'}
              />
            </div>

            {/*
             * Only when it happened. On a normal day this row is absent, which
             * is the useful signal — a permanent "0 captured offline" trains
             * the eye to skip the line on the day it finally says something.
             */}
            {route.summary.offlinePointCount > 0 ? (
              <p className="mt-2 rounded-lg border border-track-stopped/30 bg-track-stopped/5 px-3 py-2 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">
                  {route.summary.offlinePointCount} of {route.summary.pointCount} fixes
                </span>{' '}
                were captured with no connectivity and uploaded later
                {route.offlineRuns.length > 1
                  ? ` across ${route.offlineRuns.length} stretches`
                  : ''}
                , the furthest behind by{' '}
                <span className="font-medium text-foreground">
                  {formatDuration(route.summary.maxSyncLagSeconds)}
                </span>
                . The positions are as accurate as any other — GPS does not need
                the network — but they reached this panel after the fact, so
                nobody could have acted on them at the time.
              </p>
            ) : null}
          </section>

          <section>
            <h3 className="mb-2 text-xs font-medium text-muted-foreground">
              Timeline — click a band to jump the replay there
            </h3>
            <DayTimeline
              segments={route.segments}
              timezone={route.timezone}
              onSeek={onSeek}
            />
          </section>

          {speedSeries.length > 1 ? (
            <Trend
              title="Speed"
              unit="km/h"
              tone="moving"
              points={speedSeries}
              maxGapMs={GAP_BREAK_MS}
              format={(v) => String(Math.round(v))}
              {...(route.movement.avgSpeedMps !== null
                ? {
                    reference: {
                      value: route.movement.avgSpeedMps * 3.6,
                      label: 'avg while moving',
                    },
                  }
                : {})}
            />
          ) : null}

          {batterySeries.length > 1 ? (
            <Trend
              title="Battery"
              unit="%"
              points={batterySeries}
              maxGapMs={GAP_BREAK_MS}
              format={(v) => String(Math.round(v))}
            />
          ) : null}
        </>
      )}
    </div>
  );
}

function StopsTab({
  route,
  loading,
  onFocusStop,
}: {
  route: RouteDto | null;
  loading: boolean;
  onFocusStop: (index: number) => void;
}) {
  if (loading) {
    return <p className="py-6 text-center text-sm text-muted-foreground">Loading stops…</p>;
  }

  if (!route || route.stops.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border py-6 text-center">
        <p className="text-sm font-medium">No stops on this day</p>
        <p className="mt-1 text-xs text-muted-foreground">
          A stop is five minutes or more inside a 50 m circle. Shorter halts are counted as
          paused time instead.
        </p>
      </div>
    );
  }

  return (
    <ol className="space-y-2">
      {route.stops.map((stop) => (
        <li key={stop.index}>
          <button
            type="button"
            onClick={() => onFocusStop(stop.index)}
            className="flex w-full items-start gap-3 rounded-lg border border-border p-3 text-left transition-colors hover:bg-muted/60"
          >
            <span
              className={cn(
                'mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full text-2xs font-semibold text-white',
                stop.type === 'long_stop' ? 'bg-track-stopped' : 'bg-track-idle',
              )}
            >
              {stop.index + 1}
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex items-baseline justify-between gap-2">
                <span className="truncate text-sm font-medium">
                  {stop.place ?? formatCoordinates(stop.latitude, stop.longitude)}
                </span>
                <span className="shrink-0 text-xs font-semibold tabular-nums text-track-stopped">
                  {formatDuration(stop.durationSeconds)}
                </span>
              </span>
              <span className="mt-0.5 block text-2xs tabular-nums text-muted-foreground">
                {formatClock(stop.startedAt, route.timezone)} –{' '}
                {stop.isOpen ? 'still there' : formatClock(stop.endedAt, route.timezone)} ·{' '}
                {stop.pointCount} fixes · ±{stop.radiusMeters} m
              </span>
              {stop.type === 'long_stop' ? (
                <span className="mt-1 inline-block">
                  <Badge tone="stopped">Long stop</Badge>
                </span>
              ) : null}
            </span>
          </button>
        </li>
      ))}
    </ol>
  );
}

/**
 * When today's data actually reached the panel.
 *
 * This tab exists because the map alone cannot answer the question it raises.
 * A coloured trail says "this stretch arrived late"; what an operations manager
 * then needs is *when* it arrived, *how much* came at once, and *how long the
 * live map was wrong* — three facts that are timestamps and counts, not
 * geometry. Every row here frames its own stretch on the map, so the two views
 * are one investigation rather than two.
 */
function DeliveryTab({
  route,
  loading,
  onFocusRange,
  onShowDelivery,
}: {
  route: RouteDto | null;
  loading: boolean;
  onFocusRange: (range: IndexRange) => void;
  onShowDelivery: (only?: PointDelivery) => void;
}) {
  if (loading) {
    return <p className="py-6 text-center text-sm text-muted-foreground">Loading the day…</p>;
  }

  if (!route || route.points.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-border py-6 text-center text-sm text-muted-foreground">
        No fixes recorded on this day.
      </p>
    );
  }

  const { summary } = route;
  const late = summary.delayedPointCount + summary.backfillPointCount;
  const backfillRuns = route.deliveryRuns.filter((r) => r.kind === 'backfill');
  const lateUploads = route.uploads.filter((u) => u.kind !== 'live');

  return (
    <div className="space-y-5">
      <section>
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-xs font-medium text-muted-foreground">How today arrived</h3>
          <button
            type="button"
            onClick={() => onShowDelivery()}
            className="rounded-md border border-border px-2 py-1 text-2xs transition-colors hover:bg-muted"
          >
            Colour the map by this
          </button>
        </div>

        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <DeliveryMetric
            kind="live"
            count={summary.livePointCount}
            total={summary.pointCount}
            onClick={() => onShowDelivery('live')}
          />
          <DeliveryMetric
            kind="delayed"
            count={summary.delayedPointCount}
            total={summary.pointCount}
            onClick={() => onShowDelivery('delayed')}
          />
          <DeliveryMetric
            kind="backfill"
            count={summary.backfillPointCount}
            total={summary.pointCount}
            onClick={() => onShowDelivery('backfill')}
          />
          <Metric
            label="Map was blind"
            value={formatDuration(summary.darkSeconds)}
            tone={summary.darkSeconds > 0 ? 'gap' : undefined}
            sub={`worst lag ${formatDuration(summary.maxSyncLagSeconds)}`}
          />
        </div>

        <p className="mt-2 text-xs text-muted-foreground">
          {late === 0 ? (
            <>
              Everything recorded today reached this panel while it was still current. The map you
              were looking at was the map that was true.
            </>
          ) : (
            <>
              <span className="font-medium text-foreground">
                {late} of {summary.pointCount} fixes
              </span>{' '}
              reached the panel after the fact
              {summary.backfillUploadCount > 0 ? (
                <>
                  {' '}
                  across{' '}
                  <span className="font-medium text-foreground">
                    {summary.backfillUploadCount} bulk upload
                    {summary.backfillUploadCount === 1 ? '' : 's'}
                  </span>
                </>
              ) : null}
              . For{' '}
              <span className="font-medium text-foreground">
                {formatDuration(summary.darkSeconds)}
              </span>{' '}
              the live map showed a position the employee had already left. The positions
              themselves are as accurate as any other — GPS comes from satellites, not from the
              network — but nobody could have acted on them at the time.
            </>
          )}
        </p>

        {/*
         * Two different claims, and they disagree often enough to be worth
         * separating. `offlinePointCount` is the phone's own report of its
         * radio; the counts above are measured at both ends. A day where the
         * first is zero and the second is not means the phone believed it was
         * connected and was not — a captive portal, an expired token, our own
         * server down — and that is a different fault with a different fix.
         */}
        {summary.offlinePointCount !== summary.backfillPointCount + summary.delayedPointCount ? (
          <p className="mt-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-2xs text-muted-foreground">
            The device reported no signal for{' '}
            <span className="font-medium text-foreground">
              {summary.offlinePointCount} fix{summary.offlinePointCount === 1 ? '' : 'es'}
            </span>
            , while {late} arrived late.{' '}
            {summary.offlinePointCount < late
              ? 'The difference is data the phone believed it could send and could not — a captive portal, an expired sign-in, or this server being unreachable.'
              : 'The difference is data captured with no signal that still reached us promptly once the phone reconnected.'}
          </p>
        ) : null}
      </section>

      <section>
        <h3 className="mb-2 text-xs font-medium text-muted-foreground">
          {lateUploads.length > 0
            ? 'Late arrivals — click to frame the stretch on the map'
            : 'Upload timeline'}
        </h3>

        {lateUploads.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
            Every batch today landed within{' '}
            {formatDuration(Math.max(60, summary.maxSyncLagSeconds))} of capture. Nothing was
            uploaded in bulk after the fact.
          </p>
        ) : (
          <ol className="space-y-2">
            {lateUploads.map((upload, i) => {
              // The run this push carried, so clicking the row can frame it.
              const run =
                backfillRuns.find(
                  (r) => r.uploadedAt >= upload.coversFrom && r.to <= upload.coversTo,
                ) ??
                route.deliveryRuns.find(
                  (r) => r.from >= upload.coversFrom && r.to <= upload.coversTo,
                ) ??
                null;

              return (
                <li key={`${upload.uploadedAt}-${i}`}>
                  <button
                    type="button"
                    disabled={!run}
                    onClick={() => run && onFocusRange(run)}
                    className={cn(
                      'flex w-full items-start gap-3 rounded-lg border border-border p-3 text-left transition-colors',
                      run ? 'hover:bg-muted/60' : 'cursor-default',
                    )}
                  >
                    <span
                      className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ background: DELIVERY_CSS[upload.kind] }}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline justify-between gap-2">
                        <span className="truncate text-sm font-medium">
                          {formatClock(upload.uploadedAt, route.timezone)} ·{' '}
                          {upload.pointCount} fix{upload.pointCount === 1 ? '' : 'es'} arrived
                        </span>
                        <span
                          className="shrink-0 text-xs font-semibold tabular-nums"
                          style={{ color: DELIVERY_CSS[upload.kind] }}
                        >
                          {formatLag(upload.maxLagSeconds)}
                        </span>
                      </span>
                      <span className="mt-0.5 block text-2xs tabular-nums text-muted-foreground">
                        covering {formatClock(upload.coversFrom, route.timezone)} –{' '}
                        {formatClock(upload.coversTo, route.timezone)}
                        {upload.batchCount > 1 ? ` · ${upload.batchCount} batches` : ''}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        )}
      </section>

      {route.offlineRuns.length > 0 ? (
        <section>
          <h3 className="mb-2 text-xs font-medium text-muted-foreground">
            Recorded with no signal
          </h3>
          <ol className="space-y-2">
            {route.offlineRuns.map((run, i) => (
              <li key={`offline-${i}`}>
                <button
                  type="button"
                  onClick={() => onFocusRange(run)}
                  className="flex w-full items-baseline justify-between gap-2 rounded-lg border border-border p-3 text-left text-xs transition-colors hover:bg-muted/60"
                >
                  <span className="tabular-nums">
                    {formatClock(run.from, route.timezone)} –{' '}
                    {formatClock(run.to, route.timezone)}
                  </span>
                  <span className="shrink-0 tabular-nums text-muted-foreground">
                    {run.pointCount} fix{run.pointCount === 1 ? '' : 'es'}
                  </span>
                </button>
              </li>
            ))}
          </ol>
        </section>
      ) : null}
    </div>
  );
}

/** A delivery count that doubles as a filter button for the map. */
function DeliveryMetric({
  kind,
  count,
  total,
  onClick,
}: {
  kind: PointDelivery;
  count: number;
  total: number;
  onClick: () => void;
}) {
  const share = total > 0 ? Math.round((count / total) * 100) : 0;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={count === 0}
      className={cn(
        'rounded-lg border border-border p-2.5 text-left transition-colors',
        count > 0 ? 'hover:bg-muted/60' : 'cursor-default opacity-60',
      )}
    >
      <span className="flex items-center gap-1.5 text-2xs text-muted-foreground">
        <span
          className="inline-block h-2 w-2 shrink-0 rounded-full"
          style={{ background: DELIVERY_CSS[kind] }}
        />
        {DELIVERY_LABEL[kind]}
      </span>
      <span className="mt-0.5 block text-lg font-semibold tabular-nums leading-tight">{count}</span>
      <span className="block text-2xs tabular-nums text-muted-foreground">{share}% of the day</span>
    </button>
  );
}

const DIRECTION_MARK: Record<string, string> = {
  outgoing: '↗',
  incoming: '↙',
  missed: '✕',
  rejected: '✕',
  blocked: '⃠',
  voicemail: '☏',
  unknown: '•',
};

function CallsTab({ calls }: { calls: CallActivityDto | null }) {
  if (!calls) {
    return <p className="py-6 text-center text-sm text-muted-foreground">Loading calls…</p>;
  }

  const { summary } = calls;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Metric label="Total" value={summary.total} />
        <Metric label="Outgoing" value={summary.outgoing} />
        <Metric label="Incoming" value={summary.incoming} />
        <Metric
          label="Missed"
          value={summary.missed}
          sub={`${formatDuration(summary.totalDurationSeconds)} talk time`}
        />
      </div>

      {calls.calls.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
          No calls recorded on this day. Call sharing stays off unless the employee has consented
          on their device.
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {calls.calls.slice(0, 40).map((c, i) => (
            <li key={i} className="flex items-baseline justify-between gap-3 px-3 py-2 text-xs">
              <span className="min-w-0 truncate">
                <span className="mr-1.5 text-muted-foreground">
                  {DIRECTION_MARK[c.direction] ?? '•'}
                </span>
                {c.contactName || c.number || '(withheld)'}
              </span>
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {formatClock(c.startedAt, calls.timezone)}
                {c.durationSeconds > 0 ? ` · ${formatDuration(c.durationSeconds)}` : ''}
              </span>
            </li>
          ))}
          {calls.calls.length > 40 ? (
            <li className="px-3 py-2 text-2xs text-muted-foreground">
              +{calls.calls.length - 40} more not shown
            </li>
          ) : null}
        </ul>
      )}
    </div>
  );
}
